<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Detalle del grupo de cuotas</h1>
    </div>

    <div v-if="movements.length > 0">

      <!-- Header cards -->
      <div class="row mb-4">
        <div class="col-md-6">
          <b-card tag="article" no-body class="custom-card">
            <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
              <h3>Información del movimiento</h3>
            </b-card-header>
            <b-card-body class="custom-card-body">
              <table class="table table-sm table-borderless mb-0">
                <tbody>
                  <tr>
                    <th class="text-muted" style="width:40%">Colaborador</th>
                    <td>{{ header.employeeName }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">DNI/Doc</th>
                    <td>{{ header.employeeIdentifier }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Empresa</th>
                    <td>{{ header.companyName || '-' }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Área</th>
                    <td>{{ header.areaName || '-' }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Concepto</th>
                    <td>
                      {{ header.conceptName }}
                      <span :class="`badge ml-2 ${header.conceptType === 'DESCUENTO' ? 'badge-danger' : 'badge-green'}`">
                        {{ header.conceptType === 'DESCUENTO' ? 'Descuento' : 'Adicional' }}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th class="text-muted">Monto total</th>
                    <td>{{ formatAmount(header.totalAmount, header.currency) }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">N° cuotas</th>
                    <td>{{ header.totalInstallments }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Moneda</th>
                    <td>{{ header.currency }}</td>
                  </tr>
                  <tr v-if="firstDescription">
                    <th class="text-muted">Descripción</th>
                    <td>{{ firstDescription }}</td>
                  </tr>
                </tbody>
              </table>
            </b-card-body>
          </b-card>
        </div>

        <div class="col-md-6">
          <!-- Comprobante -->
          <b-card tag="article" no-body class="custom-card mb-3">
            <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
              <h3>Comprobante</h3>
            </b-card-header>
            <b-card-body class="custom-card-body">
              <table class="table table-sm table-borderless mb-0">
                <tbody>
                  <tr>
                    <th class="text-muted" style="width:40%">Tipo</th>
                    <td>{{ header.voucherType || '-' }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">N° comprobante</th>
                    <td>{{ header.voucherNumber || '-' }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Archivo</th>
                    <td>
                      <a v-if="header.attachmentUrl" href="#" @click.prevent="downloadFile" class="text-primary">
                        <i class="ri-file-line mr-1"></i>{{ header.attachmentName || 'Descargar' }}
                      </a>
                      <span v-else class="text-muted">Sin archivo</span>
                    </td>
                  </tr>
                  <tr>
                    <th class="text-muted">Registrado por</th>
                    <td>{{ header.createdBy }}</td>
                  </tr>
                </tbody>
              </table>
            </b-card-body>
          </b-card>

          <!-- Resumen -->
          <b-card tag="article" no-body class="custom-card">
            <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
              <h3>Resumen</h3>
            </b-card-header>
            <b-card-body class="custom-card-body">
              <table class="table table-sm table-borderless mb-0">
                <tbody>
                  <tr>
                    <th class="text-muted" style="width:40%">Cuotas pagadas</th>
                    <td>{{ paidCount }} de {{ header.totalInstallments }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Monto pagado</th>
                    <td>{{ formatAmount(paidAmount, header.currency) }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Saldo pendiente</th>
                    <td>{{ formatAmount(pendingAmount, header.currency) }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Estado</th>
                    <td>
                      <span :class="`badge ${groupStatusColor}`">{{ groupStatusLabel }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </b-card-body>
          </b-card>
        </div>
      </div>

      <!-- Cronograma de cuotas -->
      <b-card tag="article" no-body class="custom-card mb-4">
        <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center justify-content-between">
          <h3>Cronograma de cuotas</h3>
          <span class="text-muted small">{{ movements.length }} cuota(s)</span>
        </b-card-header>
        <b-card-body class="custom-card-body p-0">
          <table class="custom-table w-100">
            <thead>
              <tr>
                <th>N°</th>
                <th>PERÍODO</th>
                <th class="text-right">MONTO</th>
                <th>ESTADO</th>
                <th>DESCRIPCIÓN</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="m in movements" :key="m.id">
                <td>{{ m.installmentNumber }}</td>
                <td>{{ getMonthName(m.periodMonth) }} {{ m.periodYear }}</td>
                <td class="text-right">{{ formatAmount(m.amount, m.currency) }}</td>
                <td>
                  <span :class="`badge ${getMovementStatusColor(m.status)}`">{{ m.status }}</span>
                </td>
                <td>{{ m.description || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </b-card-body>
      </b-card>

      <!-- Actions -->
      <div class="d-flex justify-content-between">
        <router-link :to="{ name: 'payroll-movement-list' }" class="button button-light">Volver</router-link>
        <button
          v-if="pendingCount > 0"
          type="button"
          class="button button-danger"
          @click="openCancelModal"
        >
          <i class="ri-close-circle-line mr-1"></i>Cancelar cuotas pendientes
        </button>
      </div>
    </div>

    <!-- Cancel modal -->
    <b-modal v-model="cancelModal" title="Cancelar cuotas pendientes" hide-footer>
      <p>
        ¿Está seguro que desea cancelar las cuotas pendientes?
        Se cancelarán <strong>{{ pendingCount }}</strong> cuota(s) por un total de
        <strong>{{ formatAmount(pendingAmount, header ? header.currency : 'PEN') }}</strong>.
      </p>
      <div class="d-flex justify-content-end mt-3">
        <button type="button" class="button button-light mr-2" @click="cancelModal = false">Cerrar</button>
        <button type="button" class="button button-danger" @click="doCancelInstallments">Confirmar cancelación</button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Alert } from 'wize-admin'
import { PayrollMovementService } from '@/core/services'
import { Constants as LocalConstants } from '@/core/utils'

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

export default {
  name: 'PayrollMovementGroupDetail',

  data() {
    return {
      movements:   [],
      cancelModal: false,
    }
  },

  computed: {
    header() {
      return this.movements.length > 0 ? this.movements[0] : {}
    },
    firstDescription() {
      if (!this.header.description) return null
      const desc = this.header.description
      const prefix = `Cuota 1/${this.header.totalInstallments} - `
      return desc.startsWith(prefix) ? desc.substring(prefix.length) : desc
    },
    paidCount() {
      return this.movements.filter(m => m.status === 'PROCESADO').length
    },
    pendingCount() {
      return this.movements.filter(m => m.status === 'PENDIENTE').length
    },
    paidAmount() {
      return this.movements
        .filter(m => m.status === 'PROCESADO')
        .reduce((sum, m) => sum + Number(m.amount), 0)
    },
    pendingAmount() {
      return this.movements
        .filter(m => m.status === 'PENDIENTE')
        .reduce((sum, m) => sum + Number(m.amount), 0)
    },
    groupStatus() {
      const total = this.movements.length
      const cancelled = this.movements.filter(m => m.status === 'CANCELADO').length
      if (cancelled === total) return 'CANCELADO'
      if (this.pendingCount === 0) return 'COMPLETADO'
      return 'EN_CURSO'
    },
    groupStatusLabel() {
      const s = LocalConstants.InstallmentGroupStatus[this.groupStatus]
      return s ? s.label : this.groupStatus
    },
    groupStatusColor() {
      const s = LocalConstants.InstallmentGroupStatus[this.groupStatus]
      return s ? s.color : 'badge-gray'
    },
  },

  methods: {
    formatAmount(amount, currency) {
      if (amount == null) return '-'
      const formatted = Number(amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      return `${currency || 'S/'} ${formatted}`
    },
    getMonthName(month) {
      return MONTH_NAMES[(month || 1) - 1]
    },
    getMovementStatusColor(status) {
      const s = LocalConstants.PayrollMovementStatus[status]
      return s ? s.color : 'badge-gray'
    },

    openCancelModal() {
      this.cancelModal = true
    },

    async doCancelInstallments() {
      try {
        await this.$store.dispatch('app/loading', true)
        await PayrollMovementService.cancelFutureInstallments(this.$route.params.groupId)
        this.cancelModal = false
        Alert.success('Cuotas pendientes canceladas correctamente')
        await this.load()
      } catch (ex) {
        Alert.error(ex.errors ? ex.errors.message : 'Error al cancelar las cuotas')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },

    async downloadFile() {
      try {
        await this.$store.dispatch('app/loading', true)
        const resp = await PayrollMovementService.downloadAttachment(this.header.id)
        const url = URL.createObjectURL(new Blob([resp.data]))
        const a = document.createElement('a')
        a.href = url
        a.download = this.header.attachmentName || 'adjunto'
        a.click()
        URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar el archivo')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },

    async load() {
      try {
        await this.$store.dispatch('app/loading', true)
        const resp = await PayrollMovementService.getInstallmentGroup(this.$route.params.groupId)
        this.movements = resp.payload || []
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el grupo de cuotas')
        this.$router.push({ name: 'payroll-movement-list' })
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },

  created() {
    this.load()
  },
}
</script>

<style lang="stylus" scoped></style>
